import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Receipt } from 'lucide-react';
import type { AppointmentListItem } from '@/hooks/useAppointments';
import type { CalendarAppointmentMeta } from '@/hooks/useCalendarAppointmentMeta';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaymentChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import {
  calendarJournalIsCompleted,
  getCalendarBillingPresentation,
} from '@/utils/appointmentCalendarMeta';
import {
  appointmentJournalRailKind,
  getCalendarLifecycleStatus,
} from '@/utils/appointmentCalendarStyle';
import { appointmentStatusLabelKey } from '@/utils/appointmentStatus';
import { appointmentDateLocale, formatAppointmentTimeRange } from '@/utils/appointmentTimeFormat';
import { CLINIC_TIMEZONE, clinicStartOfDay, sameClinicDay } from '@/utils/calendarClinicTime';
import { cn } from '@/utils/cn';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';

interface AgendaViewProps {
  appointments: AppointmentListItem[];
  metaById?: Map<string, CalendarAppointmentMeta>;
  onAppointmentClick: (id: string) => void;
}

function lifecycleTone(status: ReturnType<typeof getCalendarLifecycleStatus>): StatusTone {
  switch (status) {
    case 'upcoming':
    case 'checked_in':
      return 'upcoming';
    case 'under_treatment':
      return 'coral';
    case 'treatment_finished':
    case 'completed':
      return 'completed';
    case 'no_show':
      return 'no_show';
    case 'cancelled':
      return 'cancelled';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function AgendaView({ appointments, metaById, onAppointmentClick }: AgendaViewProps) {
  const { t, i18n } = useTranslation();

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort(
      (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
  }, [appointments]);

  const groupedByDay = useMemo(() => {
    const groups: Array<{ date: Date; appointments: AppointmentListItem[] }> = [];
    let currentDate: Date | null = null;
    let currentGroup: AppointmentListItem[] = [];

    for (const apt of sortedAppointments) {
      const aptDate = clinicStartOfDay(new Date(apt.starts_at));

      if (currentDate === null || !sameClinicDay(currentDate, aptDate)) {
        if (currentDate !== null) {
          groups.push({ date: currentDate, appointments: currentGroup });
        }
        currentDate = aptDate;
        currentGroup = [apt];
      } else {
        currentGroup.push(apt);
      }
    }

    if (currentDate !== null && currentGroup.length > 0) {
      groups.push({ date: currentDate, appointments: currentGroup });
    }

    return groups;
  }, [sortedAppointments]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(appointmentDateLocale(i18n.language), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: CLINIC_TIMEZONE,
      }),
    [i18n.language],
  );

  if (appointments.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Calendar className="h-7 w-7" />}
          title={t('appointments.no_appointments')}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-s4">
      {groupedByDay.map((group) => (
        <section key={group.date.toISOString()}>
          <div className="mb-s2 flex items-center gap-s2">
            <h3 className={cn(TYPE.section, 'text-ds-text')}>{dateFormatter.format(group.date)}</h3>
            <span
              className={cn(
                TYPE.meta,
                'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-ds-neutral-tint px-s2 tabular-nums text-ds-muted',
              )}
            >
              {group.appointments.length}
            </span>
          </div>
          <Card className="overflow-hidden p-0">
            {group.appointments.map((apt) => {
              const start = new Date(apt.starts_at);
              const end = new Date(apt.ends_at);
              const meta = metaById?.get(apt.id);
              const billing = meta?.billing ?? getCalendarBillingPresentation(apt);
              const billingMethod = billing.method
                ? t(`appointments.card.billing_method.${billing.method}`, {
                    defaultValue: billing.method.replaceAll('_', ' '),
                  })
                : '';
              const billingStatus = t(`appointments.card.billing_status.${billing.status}`, {
                defaultValue: billing.status.replaceAll('_', ' '),
              });
              const billingLabel = t(`appointments.card.${billing.labelKey}`, {
                method: billingMethod,
                status: billingStatus,
              });
              const railKind = appointmentJournalRailKind({
                status: apt.status,
                journalFinalized: calendarJournalIsCompleted(meta?.journal),
              });
              const lifecycle = getCalendarLifecycleStatus(apt);
              const cancelled = apt.status === 'cancelled';

              return (
                <button
                  key={apt.id}
                  type="button"
                  onClick={() => onAppointmentClick(apt.id)}
                  className={cn(
                    'relative flex w-full items-center gap-s4 border-b border-ds-line px-s4 py-s4 text-left last:border-b-0',
                    'before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-[""]',
                    railKind === 'finalized' && 'before:bg-ds-teal',
                    railKind === 'draft' && 'before:bg-ds-teal-line',
                    railKind === 'cancelled' && 'before:bg-ds-line-strong',
                    cancelled && 'bg-ds-sunk',
                  )}
                >
                  <span className={cn(TYPE.small, TYPE_TABULAR, 'w-24 shrink-0 text-ds-muted')}>
                    {formatAppointmentTimeRange(start, end, i18n.language, CLINIC_TIMEZONE)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        TYPE.body,
                        'font-semibold text-ds-text',
                        cancelled && 'text-ds-muted line-through',
                      )}
                    >
                      {apt.patient_name}
                    </div>
                    {apt.service_name ? (
                      <div className={cn(TYPE.small, 'text-ds-muted')}>{apt.service_name}</div>
                    ) : null}
                    {apt.course_badge ? (
                      <div className={cn(TYPE.small, 'text-ds-muted')}>
                        {t('appointments.course_badge', {
                          n: apt.course_badge.index,
                          total: apt.course_badge.total,
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-s2">
                    <StatusChip tone={lifecycleTone(lifecycle)}>
                      {t(appointmentStatusLabelKey(lifecycle))}
                    </StatusChip>
                    {billing.status !== 'none' ? (
                      <PaymentChip icon={<Receipt className="h-3 w-3" aria-hidden />}>
                        {billingLabel}
                      </PaymentChip>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </Card>
        </section>
      ))}
    </div>
  );
}
